import { useQuery } from "@apollo/client";
import { Alert, Box, Button, Chip, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-premium";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { DEFAULT_EMAIL_LINK_TEXT } from "encamp-shared/src/sendGrid/types";
import { gql } from "generated-graphql";
import { StaffNotificationDetailQuery } from "generated-graphql/graphql";

import PreviewPane from "../MailRoom/Communications/CreateCommunications/components/EmailEditor/PreviewPane";
import {
  emailShortStatus,
  emailStatus,
  exclusionReasonLabel,
  inAppChip,
  inAppText,
  inclusionReasonLabel,
  suppressionLabel,
} from "./notificationRecipientLabels";

const STAFF_NOTIFICATION_EMAIL = gql(`
  query StaffNotificationEmail($id: ID!) {
    staffNotificationEmail(id: $id) {
      id
      toAddress
      subject
      bodyText
      hyperlink
      linkText
      usedTemplate
      createdAt
    }
  }
`);

export type Recipient = NonNullable<
  StaffNotificationDetailQuery["staffNotificationDetail"]
>["recipients"][number];

type RecipientRow = Recipient & { id: string };

/** Keeps the drawer light for alerts that reach hundreds of people. */
export const RECIPIENT_PAGE_SIZE = 25;

const EMPTY = "—";

function personName(recipient: Recipient): string {
  return recipient.name ?? recipient.email ?? "";
}

function isOutsideOrganization(recipient: Recipient): boolean {
  return !recipient.isStaff && recipient.exclusionReason === "NOT_IN_ORGANIZATION";
}

/** Encamp staff first, then the organization's own people, then outsiders, each by name. */
function audienceRank(recipient: Recipient): number {
  if (recipient.isStaff) {
    return 0;
  }
  return isOutsideOrganization(recipient) ? 2 : 1;
}

function toRows(recipients: Recipient[]): RecipientRow[] {
  return recipients
    .map((recipient, index) => ({ ...recipient, id: recipient.userId ?? `deleted-${index}` }))
    .sort(
      (left, right) =>
        audienceRank(left) - audienceRank(right) ||
        personName(left).localeCompare(personName(right))
    );
}

function PersonCell({ recipient, detail }: { recipient: Recipient; detail?: string }) {
  // Someone with no name on file is already named by their email.
  const caption = [recipient.email === recipient.name ? null : recipient.email, detail]
    .filter(Boolean)
    .join(" · ");
  return (
    <Stack spacing={0.25} sx={{ py: 1, minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {recipient.name ?? "Deleted user"}
        </Typography>
        {recipient.isStaff && <Chip label="Encamp" size="small" color="info" variant="outlined" />}
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ overflowWrap: "anywhere", whiteSpace: "normal" }}
      >
        {caption || EMPTY}
      </Typography>
    </Stack>
  );
}

function InAppCell({ recipient }: { recipient: Recipient }) {
  const { label, tone } = inAppChip(recipient);
  return (
    <Tooltip title={`In-app: ${inAppText(recipient)}`}>
      <Chip
        label={label}
        size="small"
        sx={
          tone === "default"
            ? undefined
            : { color: `${tone}.main`, bgcolor: (theme) => alpha(theme.palette[tone].main, 0.08) }
        }
      />
    </Tooltip>
  );
}

function EmailCell({
  recipient,
  byLegacyDigest,
}: {
  recipient: Recipient;
  byLegacyDigest: boolean;
}) {
  const { text, problem } = emailShortStatus(recipient, byLegacyDigest);
  return (
    <Tooltip title={`Email: ${emailStatus(recipient, byLegacyDigest).text}`}>
      <Typography variant="body2" color={problem ? "error" : undefined}>
        {text}
      </Typography>
    </Tooltip>
  );
}

/** Whether expanding the row shows anything its cells and tooltips don't. */
function hasMoreToShow(recipient: Recipient, byLegacyDigest: boolean): boolean {
  return (
    !!recipient.messageId ||
    !!recipient.emailHeldReason ||
    (recipient.emailAttempts ?? 0) > 1 ||
    emailStatus(recipient, byLegacyDigest).problem
  );
}

function StoredEmail({ messageId }: { messageId: string }) {
  const { data, loading, error } = useQuery(STAFF_NOTIFICATION_EMAIL, {
    variables: { id: messageId },
  });
  const email = data?.staffNotificationEmail;
  if (error) {
    return <Alert severity="error">Failed to load this email.</Alert>;
  }
  if (loading && !email) {
    return <Skeleton variant="rounded" height={200} />;
  }
  if (!email) {
    return <Alert severity="info">This email is no longer stored.</Alert>;
  }
  return (
    <Stack spacing={1}>
      <Typography variant="body2">
        <strong>To:</strong> {email.toAddress}
      </Typography>
      <Typography variant="body2">
        <strong>Subject:</strong> {email.subject}
      </Typography>
      <PreviewPane html={email.bodyText} />
      {/* The mailer's template adds the button below the body; plain emails have none. */}
      {email.usedTemplate && email.hyperlink && (
        <Box>
          <Button
            variant="contained"
            size="small"
            href={email.hyperlink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {email.linkText ?? DEFAULT_EMAIL_LINK_TEXT}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

/** Everything the row's short cells leave out, and the email as sent. */
function SentDetail({
  recipient,
  byLegacyDigest,
}: {
  recipient: Recipient;
  byLegacyDigest: boolean;
}) {
  const email = emailStatus(recipient, byLegacyDigest);
  return (
    <Stack spacing={1} sx={{ p: 2, bgcolor: "action.hover" }}>
      <Typography variant="body2">In-app: {inAppText(recipient)}</Typography>
      <Typography variant="body2" color={email.problem ? "error" : undefined}>
        Email: {email.text}
      </Typography>
      {recipient.messageId && <StoredEmail messageId={recipient.messageId} />}
    </Stack>
  );
}

const pagingProps = (rowCount: number) => ({
  autoHeight: true,
  pagination: true,
  pageSizeOptions: [RECIPIENT_PAGE_SIZE],
  initialState: { pagination: { paginationModel: { pageSize: RECIPIENT_PAGE_SIZE, page: 0 } } },
  hideFooter: rowCount <= RECIPIENT_PAGE_SIZE,
  getRowHeight: () => "auto" as const,
  disableColumnMenu: true,
  disableColumnSorting: true,
  disableColumnResize: true,
  disableRowSelectionOnClick: true,
});

export function SentToTable({
  recipients,
  byLegacyDigest,
}: {
  recipients: Recipient[];
  byLegacyDigest: boolean;
}) {
  const columns: CustomGridColDef<RecipientRow>[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      // MUI disables the toggle when there's no panel; leave the cell empty instead.
      renderCell: (params) =>
        hasMoreToShow(params.row, byLegacyDigest)
          ? GRID_DETAIL_PANEL_TOGGLE_COL_DEF.renderCell?.(params)
          : null,
    },
    {
      field: "person",
      headerName: "Person",
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }) => (
        <PersonCell
          recipient={row}
          detail={row.inclusionReasons.map(inclusionReasonLabel).join(", ")}
        />
      ),
    },
    {
      field: "inApp",
      headerName: "In-app",
      width: 100,
      renderCell: ({ row }) => <InAppCell recipient={row} />,
    },
    {
      field: "email",
      headerName: "Email",
      width: 150,
      renderCell: ({ row }) => <EmailCell recipient={row} byLegacyDigest={byLegacyDigest} />,
    },
  ];
  const rows = toRows(recipients);
  return (
    <DataGrid<RecipientRow>
      aria-label="Sent to"
      rows={rows}
      columns={columns}
      getDetailPanelContent={({ row }) =>
        hasMoreToShow(row, byLegacyDigest) ? (
          <SentDetail recipient={row} byLegacyDigest={byLegacyDigest} />
        ) : null
      }
      getDetailPanelHeight={() => "auto"}
      {...pagingProps(rows.length)}
    />
  );
}

function reasonDetail(recipient: Recipient): string | null {
  if (recipient.exclusionReason !== "ALL_CHANNELS_OFF") {
    return null;
  }
  const inAppOff = suppressionLabel(recipient.inAppSuppressedBy);
  const emailOff = suppressionLabel(recipient.emailSuppressedBy);
  const sources = [inAppOff ? `In-app ${inAppOff}` : null, emailOff ? `email ${emailOff}` : null];
  return sources.filter(Boolean).join(" · ") || null;
}

const NOT_SENT_COLUMNS: CustomGridColDef<RecipientRow>[] = [
  {
    field: "person",
    headerName: "Person",
    flex: 1,
    minWidth: 200,
    renderCell: ({ row }) => <PersonCell recipient={row} />,
  },
  {
    field: "reason",
    headerName: "Reason",
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => {
      const detail = reasonDetail(row);
      return (
        <Stack spacing={0.25} sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
            {exclusionReasonLabel(row.exclusionReason ?? "")}
          </Typography>
          {detail && (
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "normal" }}>
              {detail}
            </Typography>
          )}
        </Stack>
      );
    },
  },
];

export function NotSentTable({ recipients }: { recipients: Recipient[] }) {
  const rows = toRows(recipients);
  return (
    <DataGrid<RecipientRow>
      aria-label="Not sent"
      rows={rows}
      columns={NOT_SENT_COLUMNS}
      {...pagingProps(rows.length)}
    />
  );
}
